import React from 'react'
import { graphql, commitMutation, fetchQuery, requestSubscription } from 'react-relay'

import { SignInComponentMeQuery } from '@src/components/__generated__/SignInComponentMeQuery.graphql'
import { SignInComponentAnonMutation } from '@src/components/__generated__/SignInComponentAnonMutation.graphql'

import Env, { GraphQLError } from '@src/RelayEnvironment'
import { generateRandomString } from '@src/utils/functions'
import ConfigurationService from '@src/service/ConfigurationService'
import { refProps } from '@src/index'

import { createDebugger } from '@src/utils/debug'

import { Origin, type User, type UserInformation, addUserStat } from '@src/stores/auth/user'
import { authLogout, useAuthState, onAuthUpdated, getAuthStateIs } from '@src/stores/auth'
import { onLoginRequest, onLoginSuccess, onLoginFailure } from '@src/stores/auth'
import { useTelegramLogin } from '@src/hooks/telegram'
import { initWalletHelper } from '@src/reducers/wallet.helper'
import { initMultiplayer } from '@src/reducers/multiplayer.helper'
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { useLoginInitSubscription } from './__generated__/useLoginInitSubscription.graphql'

const fetchGlobalSettings = async () => {
  return await ConfigurationService.instance.load()
}

// Start the FingerprintJS and collect the visitor identifier
initFinger();

// Fingerprint data is stored in a global variable since it takes a while to load
// and we want to use it in the login process so we need to preload it
export let fingerprint: unknown | undefined;

// TODO: Move this elsewhere
onAuthUpdated('LoginSuccess', ({ data }) => {
  init(data.user)
  initMultiplayer()
  initWalletHelper()
})

const debug = createDebugger('hooks/init/useLogin')
export function useLogin() {
  const authState = useAuthState()
  const telegramLogin = useTelegramLogin()

  const loginTelegram = React.useCallback(async () => {
    const telegram = telegramLogin()
    if (telegram === null) return

    return await telegram
  }, [telegramLogin])

  const loginAnonymously = React.useCallback(async () => {
    if (authState.type === 'LoggedIn') {
      const { user } = authState.data

      // if we're already logged in anonymously, break out
      if (user.anonymousHash) return
      authLogout()
    }

    return await loginAnon()
  }, [])

  const loginToken = React.useCallback(async () => {
    if (authState.type !== 'LoggedOut') {
      throw new Error('Already logged in')
    }

    if (!authState.data?.token) {
      return;
    }

    const { token } = authState.data
    const { user, origin } = await fetchUserInformation()
    return { user, token, origin }
  }, [authState])

  const [initial, setInitial] = React.useState(true)
  const initialize = React.useCallback(async (): Promise<unknown> => {
    if (!initial) return
    setInitial(false)

    let lastErr;
    onLoginRequest()
    const gb$ = fetchGlobalSettings()
    for (const fn of [loginTelegram, loginToken, loginAnonymously]) {
      try {
        const result = await fn();
        if (result) {
          onLoginSuccess(result)
          await gb$
          return result
        }
      } catch (err) {
        debug.error(err)
        lastErr = err
      }
    }
    onLoginFailure(lastErr as string | Error)
    resetAuth()

    debug.error('initialize failed')
  }, [initial])

  React.useEffect(() => {
    initialize()
  }, [initialize])
}

function fetchUserInformation() {
  return new Promise<{ user: User, origin?: Origin }>((resolve, reject) => {
    // If we have token, try to log in
    return fetchQuery<SignInComponentMeQuery>(Env, meQuery, {}).subscribe({
      next: async (data) => {
        if (data.detailedMe && data.detailedMe.user) {
          const user = data.detailedMe.user as User
          if (user.anonymousHash) {
            localStorage.setItem('anonymous_hash', user.anonymousHash)
          }

          // Return both user and origin
          return resolve({
            user,
            origin: data.detailedMe.origin
          })
        } else {
          reject(new Error('Error logging in (Unknown Error)'))
        }
      },
      error: (err: GraphQLError) => {
        if (err.status === 200) {
          const errors = err.response?.json?.errors
          if (errors?.length > 0) {
            const message = errors[0].message
            if (message === 'banned') return reject(err)
          }
        }

        reject(err)
      },
    })
  })
}

function loginAnon() {
  return new Promise<UserInformation>((resolve, reject) => {
    commitMutation<SignInComponentAnonMutation>(Env, {
      mutation: makeAnonymousUser,
      variables: {
        input: {
          refProps: getReferralProps(),
          uuid: getAnonymousHash(),
          meta: fingerprint,
        },
      },
      onCompleted: async ({ getAnonymousUser }) => {
        if (!getAnonymousUser) return reject()

        const { user, token } = getAnonymousUser
        if (user === null || token === null) return reject()

        resolve({ user: user as User, token })
      },
      onError: reject,
    })
  })
}

function resetAuth() {
  localStorage.clear()
  window.location.href = '/'
}

function getAnonymousHash() {
  const existing = localStorage.getItem('anonymous_hash')
  if (existing) return existing

  const uuid = generateRandomString(25)
  localStorage.setItem('anonymous_hash', uuid)
  return uuid
}

function getReferralProps() {
  const props = [...refProps]
  const hasBonusCode = props.some((obj) => Object.hasOwn(obj, 'bonus_code'))
  const referralUuid = localStorage.getItem('referral_uuid')
  if (referralUuid && !hasBonusCode) {
    props.push({ key: 'affiliate_uuid', value: referralUuid })
  }

  return props
}

function initFinger() {
  FingerprintJS.load().then((fp) => {
    fp.get().then((result) => {
      fingerprint = JSON.stringify({
        confidence: result.confidence.score,
        fingerprint: result.visitorId,

        timezone: new Date().getTimezoneOffset() / 60,

        referrer: document.referrer,
        previousSites: window.history.length,

        browserName: navigator.appName,
        browserEngine: navigator.product,
        browserVersion1a: navigator.appVersion,
        browserVersion1b: navigator.userAgent,
        browserLanguage: navigator.language,
        browserOnline: navigator.onLine,
        browserPlatform: navigator.platform,
        javaEnabled: navigator.javaEnabled(),
        dataCookiesEnabled: navigator.cookieEnabled,

        sizeScreenW: window.screen.width,
        sizeScreenH: window.screen.height,
        sizeInW: window.innerWidth,
        sizeInH: window.innerHeight,
        sizeAvailW: window.screen.availWidth,
        sizeAvailH: window.screen.availHeight,
        scrColorDepth: window.screen.colorDepth,
        scrPixelDepth: window.screen.pixelDepth,
      })
    })
  })
}

function init(user: User) {
  requestSubscription<useLoginInitSubscription>(Env, {
    subscription: userStatUpdatedSubscription,
    variables: {},
    onNext: (data) => {
      if (data?.userStatUpdated.uuid === user.uuid) {
        if (!getAuthStateIs(['LoggedIn'])) {
          throw new Error('Not logged in')
        }

        addUserStat(data.userStatUpdated)
      }
    },
  })
}

const userStatUpdatedSubscription = graphql`
  subscription useLoginInitSubscription {
    userStatUpdated {
      uuid
      name
      value
      meta
    }
  }
`

export const meQuery = graphql`
  query useLoginMeQuery {
    detailedMe {
      origin {
        cfConnectingIp
        cfIpcountry
        cfRay
        isBlocked
      }
      user {
        name
        username
        otpEnabled
        anonymousHash
        uuid
        roles
        confirmed
        chatApproval
        insertedAt
        kycState
        refProps
        hasProfileImage
        preferences {
          key
          value
          valueJson
        }
        userStats {
          name
          value
          meta
        }
        affiliateStats {
          count
          totalCreditSumUsd
        }
        kycSuccessful
        pendingKycBonuses {
          amount
          currency
          playThrough
        }
        eliteSeat {
          currentLevel {
            name
            tierStartUsd
            tierStartLevel
            tierStep
            tierCount
            bonusUsd
          }
          nextLevel {
            name
            tierStartUsd
            tierStartLevel
            tierStep
            tierCount
            bonusUsd
          }
          currentTier
          tierProgress
          levelProgress
          pendingPayouts {
            levelNr
            bonusUsd
            dateTime
          }
          wagerAmountUsd
        }
        featureFlags
      }
    }
  }
`

export const makeAnonymousUser = graphql`
  mutation useLoginMakeAnonymousUserMutation($input: GetAnonymousUserInput!) {
    getAnonymousUser(input: $input) {
      token
      user {
        name
        username
        otpEnabled
        anonymousHash
        uuid
        roles
        confirmed
        chatApproval
        insertedAt
        kycState
        refProps
        hasProfileImage
        preferences {
          key
          value
          valueJson
        }
        userStats {
          name
          value
          meta
        }
      }
    }
  }
`
